import { useId, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, LoadingState, ReadError, StaleNotice, useAnnounce } from './PageStates';
import { formatUtc } from '../pages/auditFields';
import '../pages/Detail.css';

export const orNotSet = (value) => (value === null || value === undefined || value === '' ? 'Not set' : value);
export const when = (value, none = 'Not set') => (value ? <time dateTime={value}>{formatUtc(value)}</time> : none);
export const status = (active) => (active
  ? <span className="badge badge-active">Active</span>
  : <span className="badge badge-locked">Locked</span>);

/** Labelled key/value rows in a named region. Long values wrap (Detail.css). */
export function RecordFields({ label, rows }) {
  const id = useId();
  return (
    <section className="card record-fields" aria-labelledby={id}>
      <h3 id={id}>{label}</h3>
      <dl>{rows.map(([name, value]) => <div key={name}><dt>{name}</dt><dd>{value}</dd></div>)}</dl>
    </section>
  );
}

/** A kept record being re-read (fix round 1, M4). Stale is never set while loading, so the region says only this. */
function Refreshing() {
  useAnnounce('Refreshing');
  return <p className="field-hint">Refreshing…</p>;
}

/**
 * #583 S6b2, design §7.5 and §7.6: one detail record with the common states. The heading is there
 * from the first paint, so focus always has a home. A 404, or the server's id-validation 400, is
 * "not found", never an error. While the SAME record is re-read (after an action, or Reload) its
 * last answer stays on screen, so an open form survives, and every action is held until it lands.
 *
 * children(record, hold): hold is true while the record is loading or stale.
 */
export default function DetailPage({ screen, noun, back, title, actions, children }) {
  const focused = useRef(null);
  const heading = useRef(null);
  const [last, setLast] = useState(null);
  if (screen.data && screen.data !== last) setLast(screen.data);
  const record = screen.data ?? (screen.loading ? last : null);
  const { error } = screen;
  const notFound = error?.status === 404 || (error?.status === 400 && error.data?.type === 'Validation error');
  const name = noun.toLowerCase();
  // Remember focus before React removes a record or the browser blurs a disabled control.
  // Never steal focus that the operator has deliberately moved elsewhere.
  useLayoutEffect(() => {
    const previous = focused.current;
    const active = document.activeElement;
    if (previous && (!previous.isConnected || previous.matches(':disabled'))
      && (active === previous || active === document.body)) heading.current?.focus();
  });
  return (
    <div className="detail-page" onFocusCapture={(event) => { focused.current = event.target; }}>
      <Link className="btn btn-outline back-link" to={back.to}>{back.label}</Link>
      <h2 ref={heading} tabIndex={-1}>{notFound ? `${noun} not found` : record ? title(record) : noun}</h2>
      {screen.loading && !record && <LoadingState label={`Loading ${name}`} lines={8} />}
      {screen.loading && record && <Refreshing />}
      {notFound && <EmptyState title={`No ${name} has this id.`} />}
      {error && !notFound && <ReadError error={error} onRetry={screen.reload} />}
      {screen.stale && <StaleNotice loadedAt={screen.loadedAt} reason={screen.stale} onReload={screen.reload} />}
      {actions?.(record, !record || screen.loading || Boolean(screen.stale))}
      {record && children(record, screen.loading || Boolean(screen.stale))}
    </div>
  );
}
